/**
 * =========================================================================================
 * Pricing Plan - Order Summary (Step 2: Right Column)
 * =========================================================================================
 */

import React, { useState } from 'react';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UButton from 'ubs-framework/v2/UButton';
import UCheckBox from 'ubs-framework/v2/UCheckBox';
import { numberWithComaToFixed } from 'ubs-framework/v2/common/function';
import { dateFormatter } from 'ubs-framework/v2/common/function';
import { getBillingLabel, getDiscountForCycle } from './utils';

const OrderSummary = ({
    selectedPlan,
    priceCalc,
    previewData,
    loading,
    calcLoading,
    onSubscribe,
    currency,
    isResubscribe,
    billingCycle
}) => {
    const [warningAcknowledged, setWarningAcknowledged] = useState(false);

    const clickOnWarning = (e) => {
        e.preventDefault();
        const el = document.getElementById('resubscribe-warning-alert');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('u_pricing-plan__alert--highlight');
            setTimeout(() => el.classList.remove('u_pricing-plan__alert--highlight'), 2000);
        }
    }

    // Discount from backend priceCalc (when backend is ready)
    // Falls back to plan's discount data for display
    const discountPercent = priceCalc?.discountPercentage || getDiscountForCycle(selectedPlan?.discount, billingCycle);
    const discountAmount = priceCalc?.discountAmount || (discountPercent > 0 && priceCalc?.baseAmountBeforeDiscount ? (priceCalc.baseAmountBeforeDiscount * discountPercent / 100)?.toFixed(2) : 0);

    return (
    <div className="u_pricing-plan__invoice u_b-light">
        <h4 className="u_pricing-plan__invoice-title u_semi-large-font u_bb-light u_pb-18 u_mb-24">Order Summary</h4>

        {/* Plan Info */}
        <div className="u_pricing-plan__invoice-row u_flex-align u_content-between u_mb-12">
            <span className="u_pricing-plan__invoice-label u_small-font u_color-font">Plan</span>
            <span className="u_pricing-plan__invoice-value u_small-font u_fw-600">
                {priceCalc?.planName || selectedPlan?.title || ''}
                {(priceCalc?.planCode || selectedPlan?.plan_code) ? <span className="u_font u_ml-6 u_color-font">({priceCalc?.planCode || selectedPlan?.plan_code})</span> : null}
            </span>
        </div>

        <div className="u_pricing-plan__invoice-row u_flex-align u_content-between u_mb-16">
            <span className="u_pricing-plan__invoice-label u_small-font u_color-font">Billing Cycle</span>
            <span className="u_pricing-plan__invoice-value u_small-font u_fw-600">
                {getBillingLabel(priceCalc?.billingCycle) || selectedPlan?.billingText || ''}
                {priceCalc?.billingMultiplier > 1
                    ? <span className="u_font u_ml-6 u_color-font">({'\u00D7'}{priceCalc?.billingMultiplier} months)</span>
                    : null}
            </span>
        </div>

        <div className="u_pricing-plan__invoice-divider u_my-16" />

        {/* Pricing Breakdown */}
        <div className="u_pricing-plan__invoice-row u_flex-align u_content-between u_mb-12">
            <span className="u_pricing-plan__invoice-label u_small-font u_color-font">Per Seat Price</span>
            <span className="u_pricing-plan__invoice-value u_small-font u_fw-600">
                {numberWithComaToFixed(priceCalc?.perSeatPrice || 0, currency)} / seat
            </span>
        </div>

        <div className="u_pricing-plan__invoice-row u_flex-align u_content-between u_mb-12">
            <span className="u_pricing-plan__invoice-label u_small-font u_color-font">
                {priceCalc?.seatCount} seat{priceCalc?.seatCount > 1 ? 's' : ''} {'\u00D7'} {numberWithComaToFixed(priceCalc?.perSeatPrice || 0, currency)}
            </span>
            <span className="u_pricing-plan__invoice-value u_small-font u_fw-600">
                {numberWithComaToFixed(priceCalc?.baseAmountBeforeDiscount || priceCalc?.baseAmount || 0, currency)}
            </span>
        </div>

        {/* Discount Row */}
        {discountPercent > 0 && (
            <>
            <div className="u_pricing-plan__invoice-row u_flex-align u_content-between u_mb-12 u_py-8 u_px-10 u_bg-green-1 u_radius-5">
                <span className="u_pricing-plan__invoice-label u_small-font u_color-green u_fw-700 u_flex-align">
                    <Icon.Bonus size={14} fill="#60B158" className="u_mr-6" />
                    Discount ({discountPercent}%)
                </span>
                <span className="u_pricing-plan__invoice-value u_small-font u_fw-700 u_color-green">
                    - {numberWithComaToFixed(discountAmount, currency)}
                </span>
            </div>
            <div className="u_pricing-plan__invoice-row u_flex-align u_content-between u_mb-12">
                <span className="u_pricing-plan__invoice-label u_small-font u_color-font">Sub Total</span>
                <span className="u_pricing-plan__invoice-value u_small-font u_fw-600">
                    {numberWithComaToFixed(priceCalc?.baseAmount || 0, currency)}
                </span>
            </div>
            </>
        )}

        <div className="u_pricing-plan__invoice-row u_flex-align u_content-between u_mb-12">
            <span className="u_pricing-plan__invoice-label u_small-font u_color-font">
                GST ({priceCalc?.gstRate}%)
            </span>
            <span className="u_pricing-plan__invoice-value u_small-font u_fw-600">
                + {numberWithComaToFixed(priceCalc?.gstAmount || 0, currency)}
            </span>
        </div>

        {priceCalc?.roundOff !== 0 && (
            <div className="u_pricing-plan__invoice-row u_flex-align u_content-between u_mb-12">
                <span className="u_pricing-plan__invoice-label u_small-font u_color-font">Round Off</span>
                <span className="u_pricing-plan__invoice-value u_small-font u_fw-600">
                    {priceCalc?.roundOff > 0 ? '+' : ''}{priceCalc?.roundOff || 0}
                </span>
            </div>
        )}

        <div className="u_pricing-plan__invoice-divider u_my-16" />

        {/* Monthly recurring total */}
        <div className="u_pricing-plan__invoice-total u_flex-align u_content-between u_mb-16 u_b-light">
            <span className="u_pricing-plan__invoice-total-label u_semi-large-font">
                Total
            </span>
            <span className="u_pricing-plan__invoice-total-amount u_color-blue">
                {numberWithComaToFixed(priceCalc?.totalAmount || 0, currency)}
            </span>
        </div>

        {priceCalc?.amountInWords && !previewData?.payment?.isImmediate && (
            <p className="u_font u_mb-16 u_text-center" style={{ fontStyle: 'italic', opacity: 0.7 }}>
                {priceCalc?.amountInWords}
            </p>
        )}

        {isResubscribe && <div className="u_mb-16 u_flex-align">
            <UCheckBox
                id="resubscribe-warning"
                label=""
                checked={warningAcknowledged}
                onChange={() => setWarningAcknowledged(!warningAcknowledged)}
            />
            <span className="u_small-font">I have read and accept the above <span className="u_color-blue u_cp u_fw-600" onClick={(e) => {
                clickOnWarning(e)
            }}>warning</span>.</span>
        </div>}

        {(() => {
            const isEffectiveDowngrade = previewData?.diff?.totalAmount != null && previewData?.diff?.totalAmount < 0;
            return (
                <UButton
                    type={loading ? 'Processing...' : (previewData?.payment?.isImmediate && previewData?.payment?.immediateChargeAmount
                        ? `Pay ${numberWithComaToFixed(previewData?.payment?.immediateChargeAmount, currency)} Now`
                        : 'Subscribe Now')}
                    onClick={onSubscribe}
                    disabled={loading || calcLoading || isEffectiveDowngrade || (isResubscribe && !warningAcknowledged)}
                    cn="u_subscribe u_payment-btn"
                />
            );
        })()}

        <div className="u_pricing-plan__info u_mt-16 u_flex u_align-start">
            <Icon.InfoTooltip size={16} fill={COLOR.u_blue} />
            <p className="u_pricing-plan__info-text u_font">
                {previewData?.payment?.isImmediate
                    ? 'Your changes will take effect immediately after payment confirmation.'
                    : previewData?.payment?.effectiveDate
                        ? `Changes will be effective from ${dateFormatter({ date: previewData?.payment?.effectiveDate, format: 'DD MMM, YYYY' })}.`
                        : 'Your subscription will start immediately after payment confirmation.'
                }
            </p>
        </div>
    </div>
    );
};

export default OrderSummary;
